import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, AlertTriangle, Clock, Loader2, ShieldCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABELS = {
  player:   { title: 'Player Verification',          who: 'Coach' },
  guardian: { title: 'Parent / Guardian Consent',    who: 'Parent / Guardian' },
  club:     { title: 'Club Registration Verification', who: 'Club Manager' },
  school:   { title: 'School Registration Verification', who: 'Principal / Headmaster' },
  union:    { title: 'Union Registration Verification', who: 'Union Administrator' },
};

const ACCEPT_LABELS = {
  player:   'Confirm Player Verification',
  guardian: 'Give Parental Consent',
  club:     'Accept Club Registration',
  school:   'Accept School Registration',
  union:    'Accept Union Registration',
};

export default function VerifyLanding() {
  const { type, token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState('loading');  // loading | valid | expired | used | error
  const [data, setData] = useState(null);
  const [showAccept, setShowAccept] = useState(false);
  const [showDecline, setShowDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState(null);

  const meta = TYPE_LABELS[type];

  useEffect(() => {
    if (!meta) { setState('error'); return; }
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/verify/${type}/${token}`);
        if (!mounted) return;
        if (res.data?.state === 'valid') {
          setData(res.data);
          setState('valid');
        } else {
          setState(res.data?.state || 'error');
        }
      } catch (e) {
        if (mounted) setState('error');
      }
    })();
    return () => { mounted = false; };
  }, [type, token, meta]);

  const submit = async (action) => {
    if (action === 'accepted' && !acknowledged) {
      toast.error('Please confirm the statement first');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/verify/${type}/${token}/decision`, {
        action,
        reason: action === 'declined' ? declineReason : null,
        confirmation_statement_acknowledged: acknowledged,
      });
      setOutcome({ action, message: res.data?.message, status: res.data?.status, awaiting: res.data?.awaiting });
      setShowAccept(false);
      setShowDecline(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Render states ───
  if (state === 'loading') {
    return (
      <Centered>
        <Loader2 className="w-10 h-10 text-[#1A5C38] animate-spin" />
        <p className="mt-4 text-zinc-400">Loading verification…</p>
      </Centered>
    );
  }

  if (state === 'error' || !meta) {
    return (
      <ErrorState
        icon={<XCircle className="w-12 h-12 text-red-500" />}
        title="Invalid verification link"
        desc="This link is not recognised. Please check the URL or contact info@futurexv.co.za."
        navigate={navigate}
      />
    );
  }

  if (state === 'expired') {
    return (
      <ErrorState
        icon={<Clock className="w-12 h-12 text-yellow-500" />}
        title="This link has expired"
        desc="Verification links are valid for 7 days. The registrant can re-submit after 48 hours."
        navigate={navigate}
      />
    );
  }

  if (state === 'used') {
    return (
      <ErrorState
        icon={<CheckCircle2 className="w-12 h-12 text-zinc-400" />}
        title="This link has already been used"
        desc="A decision was already recorded on this verification request."
        navigate={navigate}
      />
    );
  }

  if (outcome) {
    const accepted = outcome.action === 'accepted';
    return (
      <Centered>
        <Card className="max-w-lg w-full bg-white border border-zinc-200 p-8 text-center" data-testid="verify-outcome-card">
          {accepted ? (
            <CheckCircle2 className="w-14 h-14 text-[#1A5C38] mx-auto" />
          ) : (
            <XCircle className="w-14 h-14 text-red-500 mx-auto" />
          )}
          <h1 className="text-2xl font-heading font-bold mt-4 text-zinc-900">
            {accepted ? 'Thank you' : 'Decision recorded'}
          </h1>
          <p className="text-zinc-600 mt-3">{outcome.message}</p>
          {outcome.awaiting && (
            <p className="text-sm text-zinc-500 mt-3">
              The account will activate once the {outcome.awaiting === 'guardian' ? 'parent/guardian' : 'coach'} also confirms.
            </p>
          )}
          <p className="text-xs text-zinc-400 mt-6">You can close this tab.</p>
        </Card>
      </Centered>
    );
  }

  // Valid → show details + accept/decline
  return (
    <div className="min-h-screen bg-zinc-50 py-12 px-4" data-testid="verify-landing-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <ShieldCheck className="w-7 h-7 text-[#1A5C38]" />
          <div>
            <h1 className="text-2xl font-heading font-bold text-zinc-900">{meta.title}</h1>
            <p className="text-sm text-zinc-500">You are reviewing as the nominated <strong>{meta.who}</strong>.</p>
          </div>
        </div>

        <Card className="bg-white border border-zinc-200 p-6 mb-6" data-testid="registrant-details-card">
          <h2 className="text-sm font-bold text-zinc-500 uppercase tracking-wider mb-4">Registrant Details</h2>
          <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-sm">
            <span className="text-zinc-500">Name</span>
            <span className="text-zinc-900 font-semibold" data-testid="registrant-name">{data.registrant.full_name}</span>
            <span className="text-zinc-500">Role</span>
            <span className="text-zinc-900 capitalize">{data.registrant.role?.replace('_', ' / ')}</span>
            <span className="text-zinc-500">Email</span>
            <span className="text-zinc-900">{data.registrant.email}</span>
          </div>
        </Card>

        <Card className="bg-[#f4f9f6] border-l-4 border-[#1A5C38] p-4 mb-6 text-sm text-zinc-700">
          <p>
            <strong>Confirmation statement:</strong> By accepting, you confirm that the information shown above is accurate
            to the best of your knowledge and that you have the authority to verify this registration as the {meta.who}.
            This decision is recorded with timestamp and is auditable.
          </p>
          {data.expires_at && (
            <p className="mt-3 text-xs text-zinc-500 flex items-center gap-1.5" data-testid="verify-expires-at">
              <Clock className="w-3.5 h-3.5" />
              This link expires on {new Date(data.expires_at).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' })}
            </p>
          )}
        </Card>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={() => setShowAccept(true)}
            className="flex-1 bg-[#1A5C38] hover:bg-[#0F3D26] text-white py-6 text-base font-bold"
            data-testid="open-accept-modal"
          >
            <CheckCircle2 className="w-5 h-5 mr-2" />
            {ACCEPT_LABELS[type]}
          </Button>
          <Button
            onClick={() => setShowDecline(true)}
            variant="outline"
            className="flex-1 border-red-300 text-red-600 hover:bg-red-50 py-6 text-base font-bold"
            data-testid="open-decline-modal"
          >
            <XCircle className="w-5 h-5 mr-2" />
            Decline
          </Button>
        </div>

        <p className="text-center text-xs text-zinc-400 mt-8">
          Need help? Contact <a href="mailto:info@futurexv.co.za" className="text-[#1A5C38]">info@futurexv.co.za</a>
        </p>
      </div>

      {/* Accept modal */}
      <Dialog open={showAccept} onOpenChange={setShowAccept}>
        <DialogContent className="bg-white border-zinc-200" data-testid="accept-confirmation-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-900">{ACCEPT_LABELS[type]}</DialogTitle>
            <DialogDescription className="text-zinc-600">
              Please tick the box below to confirm.
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-3 text-sm text-zinc-700 cursor-pointer mt-2" data-testid="confirmation-checkbox-label">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[#1A5C38]"
              data-testid="confirmation-checkbox"
            />
            <span>
              I confirm that the information shown above is accurate to the best of my knowledge and that I have the authority
              to act as the {meta.who} for this registration.
            </span>
          </label>
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowAccept(false)} data-testid="accept-cancel-button">Cancel</Button>
            <Button
              onClick={() => submit('accepted')}
              disabled={!acknowledged || submitting}
              className="bg-[#1A5C38] hover:bg-[#0F3D26] text-white"
              data-testid="accept-submit-button"
            >
              {submitting ? 'Submitting…' : 'Confirm & Submit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decline modal */}
      <Dialog open={showDecline} onOpenChange={setShowDecline}>
        <DialogContent className="bg-white border-zinc-200" data-testid="decline-confirmation-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Decline this registration?
            </DialogTitle>
            <DialogDescription className="text-zinc-600">
              The registrant will be notified and their account will not be activated.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <label className="text-sm text-zinc-700 mb-1 block">Reason (optional, helps the registrant)</label>
            <Textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="e.g. I do not recognise this player / The institution name is incorrect / I am no longer the coach"
              className="bg-white text-zinc-900 border-zinc-300"
              rows={3}
              data-testid="decline-reason-input"
            />
          </div>
          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDecline(false)} data-testid="decline-cancel-button">Cancel</Button>
            <Button
              onClick={() => submit('declined')}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="decline-submit-button"
            >
              {submitting ? 'Submitting…' : 'Confirm decline'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Centered({ children }) {
  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center px-4">
      {children}
    </div>
  );
}

function ErrorState({ icon, title, desc, navigate }) {
  return (
    <Centered>
      <Card className="max-w-md w-full bg-white border border-zinc-200 p-8 text-center" data-testid="verify-error-card">
        <div className="flex justify-center mb-4">{icon}</div>
        <h1 className="text-xl font-heading font-bold text-zinc-900">{title}</h1>
        <p className="text-sm text-zinc-600 mt-2">{desc}</p>
        <Link to="/" className="inline-block mt-6 text-sm text-[#1A5C38] hover:underline" data-testid="verify-error-home-link">
          Return to FutureXV
        </Link>
      </Card>
    </Centered>
  );
}
